import React, { useState, useEffect, useRef } from 'react';
import { Edit, RefreshCw,  Loader2, Save, X, Plus, Trash2 } from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import { aboutService, AboutSection, AboutValue } from './aboutService';

const AboutList: React.FC = () => {
  const [sections, setSections] = useState<AboutSection[]>([]);
  const [values, setValues] = useState<AboutValue[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingSection, setEditingSection] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState<number | null>(null);
  const [addingValue, setAddingValue] = useState(false);
  
  // Section edit form state
  const [sectionForm, setSectionForm] = useState({
    title: '',
    content: '',
    button_text: '',
    button_link: '',
    order: 0,
    is_active: true,
  });
  const [sectionImage, setSectionImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');

  // Value form state
  const [valueForm, setValueForm] = useState({
    value_text: '',
    value_dari: '',
    order: 0,
    is_active: true,
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [sectionsRes, valuesRes] = await Promise.all([
        aboutService.getSections(),
        aboutService.getValues()
      ]);
      
      if (sectionsRes.success) setSections(sectionsRes.data);
      if (valuesRes.success) setValues(valuesRes.data);
    } catch (error) {
      toast.error('Failed to fetch about data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // ===== SECTION HANDLERS =====
  const startEditSection = (section: AboutSection) => {
    setEditingSection(section.id);
    setSectionForm({
      title: section.title,
      content: section.content,
      button_text: section.button_text || 'Read More',
      button_link: section.button_link || '/vision',
      order: section.order || 0,
      is_active: section.is_active,
    });
    setImagePreview(section.image_url || '');
    setSectionImage(null);
  };

  const cancelEditSection = () => {
    setEditingSection(null);
    setSectionImage(null);
    setImagePreview('');
  };

  const handleSectionImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSectionImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const saveSection = async (id: number) => {
  try {
    console.log('🔵 Saving section:', id, sectionForm);
    
    const formData = new FormData();
    formData.append('title', sectionForm.title);
    formData.append('content', sectionForm.content);
    formData.append('button_text', sectionForm.button_text);
    formData.append('button_link', sectionForm.button_link);
    formData.append('order', String(sectionForm.order));
    // formData.append('is_active', String(sectionForm.is_active));
    // AboutList.tsx, inside saveSection
    formData.append('is_active', sectionForm.is_active ? '1' : '0');
    
    if (sectionImage) {
      formData.append('image', sectionImage);
    }

    // Log FormData contents
    console.log('🔵 FormData entries:');
    for (let [key, value] of formData.entries()) {
      if (value instanceof File) {
        console.log(`${key}: File - ${value.name}, ${value.size} bytes`);
      } else {
        console.log(`${key}:`, value);
      }
    }

    const response = await aboutService.updateSection(id, formData);
    console.log('🔵 Update response:', response);
    
    if (response.success) {
      toast.success('Section updated successfully');
      setEditingSection(null);
      setSectionImage(null);
      setImagePreview('');
      fetchData();
    } else  {
          toast.error('Failed to update section');
        }
      } catch (error: any) {
        console.error('🔴 Update error:', error);
        console.error('🔴 Error response:', error.response?.data);
        toast.error(error.response?.data?.message || 'Failed to update section');
      }
    };
  // ===== VALUE HANDLERS =====
  const startAddValue = () => {
    setAddingValue(true);
    setValueForm({
      value_text: '',
      value_dari: '',
      order: values.length,
      is_active: true,
    });
  };

  const startEditValue = (value: AboutValue) => {
    setEditingValue(value.id);
    setValueForm({
      value_text: value.value_text,
      value_dari: value.value_dari || '',
      order: value.order || 0,
      is_active: value.is_active,
    });
  };

  const cancelValueForm = () => {
    setAddingValue(false);
    setEditingValue(null);
  };

  const saveValue = async () => {
    try {
      if (!valueForm.value_text.trim()) {
        toast.error('Value text is required');
        return;
      }

      let response;
      if (editingValue) {
        response = await aboutService.updateValue(editingValue, valueForm);
      } else {
        response = await aboutService.createValue(valueForm);
      }
      
      if (response.success) {
        toast.success(editingValue ? 'Value updated' : 'Value added');
        cancelValueForm();
        fetchData();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to save value');
    }
  };

  const deleteValue = async (id: number) => {
    if (!confirm('Delete this value?')) return;
    try {
      const response = await aboutService.deleteValue(id);
      if (response.success) {
        toast.success('Value deleted');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to delete value');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Toaster position="top-right" />
      
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">About Page</h1>
          <p className="text-gray-600">Manage About, Vision, Mission, Values tabs</p>
        </div>
        <button onClick={fetchData} className="px-4 py-2 border rounded-lg hover:bg-gray-50 flex items-center gap-2">
          <RefreshCw size={18} />
          Refresh
        </button>
      </div>

      {/* ===== SECTIONS ===== */}
      <h2 className="text-xl font-semibold text-gray-700 mt-8">Tabs</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {sections.map((section) => (
          <div key={section.id} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            {editingSection === section.id ? (
              // Edit Mode
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input
                    type="text"
                    value={sectionForm.title}
                    onChange={(e) => setSectionForm({...sectionForm, title: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                  <textarea
                    rows={4}
                    value={sectionForm.content}
                    onChange={(e) => setSectionForm({...sectionForm, content: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Button Text</label>
                    <input
                      type="text"
                      value={sectionForm.button_text}
                      onChange={(e) => setSectionForm({...sectionForm, button_text: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Button Link</label>
                    <input
                      type="text"
                      value={sectionForm.button_link}
                      onChange={(e) => setSectionForm({...sectionForm, button_link: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Image</label>
                  <div className="flex items-center gap-3">
                    {imagePreview && (
                      <img src={imagePreview} alt="Preview" className="w-16 h-16 object-cover rounded-lg" />
                    )}
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleSectionImageChange}
                      accept="image/*"
                      className="flex-1"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={sectionForm.is_active}
                      onChange={(e) => setSectionForm({...sectionForm, is_active: e.target.checked})}
                    />
                    Active
                  </label>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => saveSection(section.id)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2">
                    <Save size={16} /> Save
                  </button>
                  <button onClick={cancelEditSection} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex items-center gap-2">
                    <X size={16} /> Cancel
                  </button>
                </div>
              </div>
            ) : (
              // View Mode
              <div>
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold text-gray-800">{section.title}</h3>
                    <p className="text-sm text-gray-500">Tab: {section.tab_key}</p>
                    <p className="text-xs text-gray-400 mt-1">Order: {section.order}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded-full text-xs ${section.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {section.is_active ? 'Active' : 'Inactive'}
                    </span>
                    <button onClick={() => startEditSection(section)} className="p-1 text-blue-600 hover:bg-blue-50 rounded">
                      <Edit size={16} />
                    </button>
                  </div>
                </div>
                <div className="mt-2 flex items-center gap-3">
                  <img src={section.image_url} alt={section.title} className="w-12 h-12 object-cover rounded-lg" />
                  <p className="text-sm text-gray-600 line-clamp-2">{section.content.substring(0, 80)}...</p>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ===== VALUES ===== */}
      <h2 className="text-xl font-semibold text-gray-700 mt-8">Values</h2>
      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <span className="text-sm text-gray-500">{values.length} values</span>
          <button 
            onClick={startAddValue} 
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm flex items-center gap-1"
          >
            <Plus size={16} /> Add Value
          </button>
        </div>

        {/* Add/Edit Value Form */}
        {(addingValue || editingValue) && (
          <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">English Text *</label>
                <input
                  type="text"
                  value={valueForm.value_text}
                  onChange={(e) => setValueForm({...valueForm, value_text: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Integrity and accountability"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Dari Translation</label>
                <input
                  type="text"
                  value={valueForm.value_dari}
                  onChange={(e) => setValueForm({...valueForm, value_dari: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., صداقت و پاسخگویی"
                />
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <button onClick={saveValue} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2">
                <Save size={16} /> {editingValue ? 'Update' : 'Add'} Value
              </button>
              <button onClick={cancelValueForm} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Values List */}
        <div className="space-y-2">
          {values.length === 0 && (
            <p className="text-center text-gray-500 py-4">No values added yet.</p>
          )}
          {values.map((value) => (
            <div key={value.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
              <div className="flex items-center gap-3">
                <span className="text-sm font-bold text-blue-600">#{value.order + 1}</span>
                <div>
                  <span className="font-medium">{value.value_text}</span>
                  {value.value_dari && (
                    <span className="text-sm text-gray-500 ml-2">({value.value_dari})</span>
                  )}
                </div>
                <span className={`px-2 py-0.5 rounded-full text-xs ${value.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {value.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => startEditValue(value)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded">
                  <Edit size={16} />
                </button>
                <button onClick={() => deleteValue(value.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-50 rounded-lg p-3 text-center text-sm text-gray-500">
        <p>✅ Connected to Laravel API | {sections.length} sections, {values.length} values loaded</p>
      </div>
    </div>
  );
};

export default AboutList;